import { useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import i18n from '../i18n/config';
import { useNotificationStore, Notification } from '../stores/notificationStore';
import { formatWsEventMessage } from '../lib/wsEventMessage';

let notificationCounter = 0;

/**
 * The live-socket envelope. Only `eventType` is guaranteed: `auth_error` and
 * `connected` carry almost nothing else, `handoff_assigned` sends `payload` as
 * a bare string, and `critical_result`/`cds_alert` omit the reference fields —
 * so everything else is optional and `payload` is widened accordingly.
 * (`targetUserIds` is never sent — the server reads snake_case
 * `target_user_ids` — but stays as an accepted optional for tolerance.)
 */
export interface WsEvent {
  eventType: string;
  priority?: string;
  payload?: Record<string, unknown> | string;
  timestamp?: string;
  sourceService?: string;
  targetUserIds?: number[];
  target_user_ids?: number[];
  referenceType?: string | null;
  referenceId?: number | null;
  /** A persisted-notification row id, when a producer includes one (see below). */
  notificationId?: number;
  notification_id?: number;
  /** Top-level notice text (`auth_error`, `connected`). */
  message?: string;
}

interface UseWebSocketOptions {
  url?: string;
  token: string | null;
  enabled?: boolean;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

/**
 * Key a live notification by its persisted row id when the envelope carries
 * one, else by the per-page-load counter. No producer sends the row id yet, so
 * today this always falls through to the counter — but the day a producer adds
 * it, the bell's items reconcile with the REST inbox (mark-read/dismiss line up
 * instead of living as two independent unread truths).
 *
 * S5-100: the counter lives in NEGATIVE space. Server row ids are positive,
 * and on a fresh database (rows from 1) a positive counter key could equal an
 * unrelated server row id — a REST mark-read would then mark the wrong bell
 * item read and deflate the badge.
 */
function wsNotificationId(data: WsEvent): number {
  const candidates = [
    data.notificationId,
    data.notification_id,
    typeof data.payload === 'object' && data.payload !== null
      ? (data.payload.notificationId ?? data.payload.notification_id)
      : undefined,
  ];
  for (const c of candidates) {
    if (typeof c === 'number' && Number.isInteger(c) && c > 0) return c;
  }
  return -(++notificationCounter);
}

export function useWebSocket({
  url = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`,
  token,
  enabled = true,
  reconnectInterval = 3000,
  maxReconnectAttempts = 10,
}: UseWebSocketOptions) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectCount = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  // Set in disconnect(): ws.close() fires onclose asynchronously, and without
  // this the handler schedules a ghost reconnect from every intentional close
  // (effect cleanup, StrictMode remount, logout navigation) — using the stale
  // closure token, which post-logout is revoked and spams auth_error items.
  const closedIntentionally = useRef(false);
  // Set on auth_error: the close that follows a refusal must not reconnect —
  // retrying with the same dead token just replays the refusal up to 10 times.
  const authRefused = useRef(false);
  // S5-042: connection generation. Every connect() mints a new one and every
  // handler ignores events from a superseded socket: without this, a token
  // rotation's cleanup+setup (synchronous, one commit) followed by socket#1's
  // async onclose nulled wsRef under healthy socket#2 and dialled a ghost
  // socket#3 with the OLD token — an immortal duplicate, or a spurious
  // session-expired banner plus a poisoned retry on a healthy session.
  const genRef = useRef(0);
  const { addNotification, setConnected, setConnectionDown, setAuthFailed, publishContextEvent, isConnected } =
    useNotificationStore();

  const connect = useCallback(() => {
    if (!token || !enabled) return;

    const gen = ++genRef.current;
    closedIntentionally.current = false;
    // A manual reconnect() (or a retry racing a slow close) must not leave
    // the previous socket open beside the new one — close it; its async
    // onclose lands with a stale generation and is ignored below.
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    // Token is sent as the first text message after the upgrade (not as
    // a query parameter) — the gateway's WS handler rejects query-
    // embedded tokens to keep them out of access/proxy logs and
    // browser history. See backend/gateway/src/api/websocket.rs.
    const ws = new WebSocket(url);

    ws.onopen = () => {
      // A superseded socket that opens late must not authenticate — close it
      // instead of sending a token the session has already moved past.
      if (gen !== genRef.current) {
        ws.close();
        return;
      }
      if (import.meta.env.DEV) console.log('[WS] Connected — sending auth token');
      ws.send(token);
      setConnected(true);
      setConnectionDown(false);
      setAuthFailed(false);
      authRefused.current = false;
      reconnectCount.current = 0;
    };

    ws.onmessage = (event) => {
      // Stale-socket traffic (including a ghost's auth_error) must neither
      // ring the bell nor poison the retry loop of the live socket.
      if (gen !== genRef.current) return;
      try {
        const data: WsEvent = JSON.parse(event.data);
        if (data.eventType === 'connected') return;

        // A refused token is a re-login signal, not an inbox item. Skip the
        // bell, poison the retry loop (the close that follows must not
        // reconnect), and tell the user what actually happened.
        if (data.eventType === 'auth_error') {
          authRefused.current = true;
          setConnected(false);
          setConnectionDown(true);
          setAuthFailed(true);
          toast.error(i18n.t('errors:sessionExpired'));
          return;
        }

        // CCOW follow-me traffic rides a dedicated channel, not the bell: each
        // routine context change used to add a junk inbox item, and formatting
        // the payload to prose at ingest made it unrecoverable to the sync
        // hook. The raw payload is published verbatim for useContextSync.
        if (data.eventType === 'context.changed') {
          publishContextEvent(data.payload);
          return;
        }

        // Convert WsEvent to Notification (matches backend NotificationResponse shape).
        // The message is formatted to human-readable text at ingest: the old
        // JSON.stringify(payload) showed clinicians raw JSON blobs in the
        // inbox. Machine-consumed events (context.changed above) bypass this.
        const notification: Notification = {
          notificationId: wsNotificationId(data),
          userId: 0, // WebSocket events don't carry user ID; set by server
          notificationType: data.eventType,
          category: 'in_app',
          priority: data.priority ?? 'normal',
          title: data.eventType.replace(/_/g, ' '),
          message: formatWsEventMessage(data.eventType, data.payload),
          sourceType: data.referenceType ?? null,
          sourceId: data.referenceId ?? null,
          isRead: false,
          readAt: null,
          expiresAt: null,
          createdAt: data.timestamp ?? new Date().toISOString(),
        };
        addNotification(notification);
      } catch (err) {
        console.error('[WS] Failed to parse message:', err);
      }
    };

    ws.onclose = () => {
      // S5-042: a superseded socket's close touches nothing — neither wsRef
      // (which may hold the healthy replacement) nor the retry timer (which
      // would dial a ghost with this closure's stale token).
      if (gen !== genRef.current) return;
      if (import.meta.env.DEV) console.log('[WS] Disconnected');
      setConnected(false);
      if (wsRef.current === ws) wsRef.current = null;

      // An intentional close (disconnect/unmount/logout) or an auth refusal
      // never reconnects — see the refs above.
      if (closedIntentionally.current || authRefused.current) return;

      // Auto-reconnect with exponential backoff. S5-101: capped at 30s — the
      // uncapped 3s·2ⁿ held the terminal banner ~51 min past the drop — with
      // ±25% jitter so a gateway restart does not retry every client in
      // lockstep and stampede each step.
      if (enabled && reconnectCount.current < maxReconnectAttempts) {
        const delay = Math.min(reconnectInterval * Math.pow(2, reconnectCount.current), 30_000);
        const jittered = delay * (0.75 + Math.random() * 0.5);
        reconnectCount.current += 1;
        if (import.meta.env.DEV) console.log(`[WS] Reconnecting in ${Math.round(jittered)}ms (attempt ${reconnectCount.current})`);
        reconnectTimer.current = setTimeout(connect, jittered);
      } else if (enabled) {
        // Retries exhausted: the socket stays dead, so say so — live
        // critical-result / CDS toasts silently stopping is the failure mode
        // the AppShell banner exists to surface.
        setConnectionDown(true);
      }
    };

    ws.onerror = (error) => {
      console.error('[WS] Error:', error);
    };

    wsRef.current = ws;
  }, [url, token, enabled, reconnectInterval, maxReconnectAttempts, addNotification, setConnected, setConnectionDown, setAuthFailed, publishContextEvent]);

  const disconnect = useCallback(() => {
    closedIntentionally.current = true;
    // S5-042: retire the generation so the async close of the socket below
    // is ignored even if a replacement connects before it lands.
    genRef.current += 1;
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnected(false);
  }, [setConnected]);

  const reconnect = useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
    }
    reconnectCount.current = 0;
    authRefused.current = false;
    setConnectionDown(false);
    setAuthFailed(false);
    connect();
  }, [connect, setConnectionDown, setAuthFailed]);

  /**
   * Best-effort send. Returns false (rather than throwing or silently
   * dropping) when the socket is not OPEN, so the next caller can queue,
   * retry, or tell the user instead of assuming delivery.
   */
  const send = useCallback((data: unknown): boolean => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  // S5-102: the store's reactive flag, not a render-time ref read — the old
  // expression evaluated once per render and never re-rendered on socket
  // transitions, so any consumer read a stale value.
  return { send, disconnect, reconnect, isConnected };
}
